import { useEffect, useRef } from "react";

import { isTypingTarget } from "@/lib/utils";

export interface ShortcutHandlers {
  readonly onCompose?: () => void;
  readonly onFocusSearch?: () => void;
  readonly onNext?: () => void;
  readonly onPrevious?: () => void;
  readonly onArchive?: () => void;
  readonly onDelete?: () => void;
  readonly onReply?: () => void;
  readonly onFlag?: () => void;
  readonly onToggleHelp?: () => void;
  readonly onEscape?: () => void;
}

function resolveHandler(event: KeyboardEvent, handlers: ShortcutHandlers): (() => void) | null {
  if (event.metaKey || event.ctrlKey || event.altKey) return null;
  switch (event.key) {
    case "?":
      return handlers.onToggleHelp ?? null;
    case "c":
      return handlers.onCompose ?? null;
    case "/":
      return handlers.onFocusSearch ?? null;
    case "j":
      return handlers.onNext ?? null;
    case "k":
      return handlers.onPrevious ?? null;
    case "e":
      return handlers.onArchive ?? null;
    case "#":
      return handlers.onDelete ?? null;
    case "r":
      return handlers.onReply ?? null;
    case "s":
      return handlers.onFlag ?? null;
    case "Escape":
      return handlers.onEscape ?? null;
    default:
      return null;
  }
}

/**
 * Global Gmail-style keyboard shortcuts. Ignored while the user is typing in a
 * form control, and while a modifier key is held (browser shortcuts win).
 */
export function useKeyboardShortcuts(handlers: ShortcutHandlers, enabled = true): void {
  const handlersRef = useRef(handlers);
  handlersRef.current = handlers;

  useEffect(() => {
    if (!enabled) return;
    const listener = (event: KeyboardEvent): void => {
      if (isTypingTarget(event.target)) return;
      const handler = resolveHandler(event, handlersRef.current);
      if (handler === null) return;
      if (event.key === "/" || event.key === "?") event.preventDefault();
      handler();
    };
    window.addEventListener("keydown", listener);
    return () => {
      window.removeEventListener("keydown", listener);
    };
  }, [enabled]);
}
